import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    CalendarClock,
    Plane,
    HeartCrack,
    BookOpen,
    XCircle,
    Pencil,
    Palette,
    Upload,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import { useAuth } from "@/contexts/AuthContext";

const DAY_LABELS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const DAY_SHORT = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];

const STATUS_LABELS = {
    work: { label: "Trabaja", short: "", color: "" },
    vacation: { label: "Vacaciones", short: "VACACIONES", color: "#FACC15" },
    sick: { label: "Baja", short: "BAJA", color: "#F87171" },
    permission: { label: "Permiso", short: "PERM", color: "#FB923C" },
    unavailable: { label: "No puede", short: "NO PUEDE", color: "#A1A1AA" },
    manual: { label: "Manual", short: "", color: "" },
};

const STATUS_OPTIONS = [
    { value: "work", label: "Trabaja (limpieza calculada)", icon: CalendarClock, hint: "Las horas se calculan automáticamente desde las limpiezas asignadas." },
    { value: "vacation", label: "Vacaciones", icon: Plane, hint: "Cuenta 0 horas ese día." },
    { value: "sick", label: "Baja", icon: HeartCrack, hint: "Introduce las horas que deberían computar (ej. 5h)." },
    { value: "permission", label: "Permiso retribuido", icon: BookOpen, hint: "Introduce las horas a computar." },
    { value: "unavailable", label: "No puede / Libre", icon: XCircle, hint: "Cuenta 0 horas. No genera computación." },
    { value: "manual", label: "Manual (horas fijas)", icon: Pencil, hint: "Sobrescribe el cálculo automático con horas fijas." },
];

const COLOR_PALETTE = [
    "#7C3AED", "#F59E0B", "#10B981", "#0EA5E9", "#EF4444",
    "#D97706", "#EC4899", "#8B5CF6", "#84CC16", "#06B6D4",
    "#F97316", "#22D3EE", "#A855F7", "#65A30D", "#E11D48",
    "#6F8F72", "#0D2419", "#92400E", "#5B21B6",
];

function fmtHHMM(min) {
    if (!min || min <= 0) return "";
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}:${m.toString().padStart(2, "0")}`;
}

function fmtHHMMSigned(min) {
    if (!min) return "0:00:00";
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    const sign = min < 0 ? "-" : "";
    return `${sign}${h}:${m.toString().padStart(2, "0")}:00`;
}

function fmtHHMMSS(min) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}:${m.toString().padStart(2, "0")}:00`;
}

function weekStartISO(d) {
    const x = new Date(d);
    const day = x.getDay() || 7;
    x.setDate(x.getDate() - day + 1);
    x.setHours(0, 0, 0, 0);
    return x.toISOString().slice(0, 10);
}

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

export default function Schedule() {
    const { user } = useAuth();
    const [ws, setWs] = useState(() => weekStartISO(new Date()));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [cellEdit, setCellEdit] = useState(null); // { emp, dateIdx, date, day }
    const [colorEdit, setColorEdit] = useState(null); // emp
    const [importOpen, setImportOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/schedule/weekly?week_start=${ws}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [ws]);

    useEffect(() => {
        load();
    }, [load]);

    const weekRange = useMemo(() => {
        if (!data) return "";
        const start = new Date(data.week_start);
        const end = new Date(data.week_end);
        const fmt = (d) => d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
        return `${fmt(start)} → ${fmt(end)}`;
    }, [data]);

    const shiftWeek = (delta) => {
        const d = new Date(ws);
        d.setDate(d.getDate() + delta * 7);
        setWs(weekStartISO(d));
    };

    if (!user || !["admin", "coordinator", "super_admin"].includes(user.role)) {
        return (
            <div className="zentra-card-static p-10 text-center" data-testid="schedule-no-access">
                <p className="font-display font-semibold text-zentra-ink">Acceso restringido</p>
                <p className="text-sm text-zentra-muted mt-1">
                    Sólo administradores y coordinadores pueden ver el horario.
                </p>
            </div>
        );
    }

    const todayIso = todayISO();

    return (
        <div className="space-y-6" data-testid="schedule-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Recursos humanos · Cuadrante semanal
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Horario del personal
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Pincha en una celda para asignar vacaciones, baja, permiso, descanso o un valor manual.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button className="zentra-btn-ghost" onClick={() => shiftWeek(-1)} data-testid="schedule-prev-week">
                        <ChevronLeft size={14} />
                    </button>
                    <button
                        className="zentra-btn-ghost"
                        onClick={() => setWs(weekStartISO(new Date()))}
                        data-testid="schedule-this-week"
                    >
                        Esta semana
                    </button>
                    <button className="zentra-btn-ghost" onClick={() => shiftWeek(1)} data-testid="schedule-next-week">
                        <ChevronRight size={14} />
                    </button>
                    <span className="text-sm font-mono-zentra text-zentra-ink ml-2 capitalize">{weekRange}</span>
                </div>
            </div>

            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
            )}

            {loading || !data ? (
                <div className="zentra-skeleton h-96" />
            ) : data.employees.length === 0 ? (
                <div className="zentra-card-static p-10 text-center">
                    <p className="text-sm text-zentra-muted">Aún no hay personal activo.</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="schedule-grid">
                    {/* Employees header */}
                    <div className="zentra-card-static overflow-x-auto sticky top-[4.2rem] z-20 bg-white">
                        <div
                            className="grid gap-px bg-zentra-border min-w-fit"
                            style={{
                                gridTemplateColumns: `170px repeat(${data.employees.length}, minmax(120px, 1fr))`,
                            }}
                        >
                            <div className="bg-white px-4 py-3 flex flex-col justify-center">
                                <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">
                                    Semana
                                </p>
                                <p className="text-[11px] text-zentra-ink mt-0.5 capitalize">{weekRange}</p>
                            </div>
                            {data.employees.map((emp) => (
                                <button
                                    type="button"
                                    key={emp.employee_id}
                                    className="bg-white hover:bg-zentra-bg/50 transition px-3 py-2.5 text-left group"
                                    onClick={() => setColorEdit(emp)}
                                    data-testid={`emp-header-${emp.employee_id}`}
                                    title="Cambiar color"
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-white shadow-sm"
                                            style={{ background: emp.color }}
                                        />
                                        <span className="text-xs font-semibold text-zentra-ink truncate">
                                            {emp.name.split(" ")[0]}
                                        </span>
                                        <Palette
                                            size={10}
                                            className="opacity-0 group-hover:opacity-60 transition ml-auto text-zentra-muted"
                                        />
                                    </div>
                                    <p className="text-[10px] text-zentra-muted font-mono-zentra mt-1">
                                        {fmtHHMMSS(emp.contracted_minutes)} /sem
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Day rows */}
                    {DAY_LABELS.map((dayLabel, di) => {
                        const dayIso = data.employees[0]?.days[di]?.date;
                        const isToday = dayIso === todayIso;
                        const isWeekend = di >= 5;
                        return (
                            <div
                                key={dayLabel}
                                className={`zentra-card-static overflow-x-auto ${isToday ? "ring-2 ring-zentra-accent" : ""}`}
                                data-testid={`schedule-day-${dayIso}`}
                            >
                                <div
                                    className="grid gap-px bg-zentra-border min-w-fit"
                                    style={{
                                        gridTemplateColumns: `170px repeat(${data.employees.length}, minmax(120px, 1fr))`,
                                    }}
                                >
                                    {/* Day label */}
                                    <div
                                        className={`px-4 py-4 flex flex-col justify-center ${
                                            isToday
                                                ? "bg-zentra-primary text-white"
                                                : isWeekend
                                                    ? "bg-zentra-bg"
                                                    : "bg-white"
                                        }`}
                                    >
                                        <p
                                            className={`text-[10px] font-semibold tracking-widest uppercase ${
                                                isToday ? "text-zentra-accent" : "text-zentra-muted"
                                            }`}
                                        >
                                            {dayLabel}
                                            {isToday && <span className="ml-2 text-zentra-accent">· HOY</span>}
                                        </p>
                                        <p
                                            className={`font-display text-xl font-bold mt-0.5 ${
                                                isToday ? "text-white" : "text-zentra-ink"
                                            }`}
                                        >
                                            {dayIso ? new Date(dayIso).getDate() : ""}
                                        </p>
                                        <p
                                            className={`text-[10px] font-mono-zentra mt-0.5 ${
                                                isToday ? "text-white/80" : "text-zentra-muted"
                                            }`}
                                        >
                                            {dayIso?.slice(5) || ""}
                                        </p>
                                    </div>

                                    {/* Cells per employee */}
                                    {data.employees.map((emp) => {
                                        const day = emp.days[di];
                                        const ov = day.override;
                                        const status = ov?.status || "work";
                                        const display = (() => {
                                            if (status === "vacation") return { label: "VAC", color: "#92400E", bg: "#FEF3C7", border: "#FCD34D" };
                                            if (status === "unavailable") return { label: "—", color: "#52525B", bg: "#F4F4F5", border: "#D4D4D8" };
                                            if (status === "sick") return { label: `BAJA ${fmtHHMM(ov.minutes)}`, color: "#7F1D1D", bg: "#FEE2E2", border: "#FCA5A5" };
                                            if (status === "permission") return { label: `PERM ${fmtHHMM(ov.minutes)}`, color: "#7C2D12", bg: "#FED7AA", border: "#FB923C" };
                                            if (status === "manual") return { label: fmtHHMM(day.minutes) || "0:00", color: "#1E3A8A", bg: "#DBEAFE", border: "#93C5FD" };
                                            // work (calculated)
                                            if (day.minutes > 0) return { label: fmtHHMM(day.minutes), color: "#064E3B", bg: "#D1FAE5", border: "#6EE7B7" };
                                            return { label: "·", color: "#A1A1AA", bg: "#FAFAFA", border: "transparent" };
                                        })();
                                        return (
                                            <button
                                                type="button"
                                                key={emp.employee_id + day.date}
                                                className={`px-2 py-4 hover:opacity-90 hover:shadow-inner transition text-center group relative`}
                                                style={{
                                                    background: display.bg,
                                                    color: display.color,
                                                    borderLeft: `3px solid ${display.border}`,
                                                }}
                                                onClick={() => setCellEdit({ emp, dateIdx: di, date: day.date, day })}
                                                data-testid={`cell-${emp.employee_id}-${day.date}`}
                                            >
                                                <span className="font-display font-bold text-sm leading-none">
                                                    {display.label}
                                                </span>
                                                {day.tasks?.length > 0 && (
                                                    <span className="block text-[9px] mt-1 opacity-70 font-mono-zentra">
                                                        {day.tasks.length} {day.tasks.length === 1 ? "tarea" : "tareas"}
                                                    </span>
                                                )}
                                                <Pencil
                                                    size={9}
                                                    className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-50 transition"
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    {/* Footer: weekly totals + cómputo */}
                    <div className="zentra-card-static overflow-x-auto bg-gradient-to-r from-zentra-primary to-[#16442d] text-white">
                        <div
                            className="grid gap-px bg-white/10 min-w-fit"
                            style={{
                                gridTemplateColumns: `170px repeat(${data.employees.length}, minmax(120px, 1fr))`,
                            }}
                        >
                            <div className="bg-transparent px-4 py-3 flex flex-col justify-center">
                                <p className="text-[10px] font-semibold tracking-widest uppercase text-white/60">
                                    Total semana
                                </p>
                                <p className="text-[10px] text-white/70 mt-0.5">Trabajadas (real)</p>
                            </div>
                            {data.employees.map((emp) => (
                                <div
                                    key={`total-${emp.employee_id}`}
                                    className="bg-transparent px-2 py-3 text-center"
                                    data-testid={`total-${emp.employee_id}`}
                                >
                                    <p className="font-display font-bold text-base text-white">
                                        {fmtHHMMSS(emp.assigned_minutes)}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <div
                            className="grid gap-px bg-white/10 min-w-fit border-t border-white/15"
                            style={{
                                gridTemplateColumns: `170px repeat(${data.employees.length}, minmax(120px, 1fr))`,
                            }}
                        >
                            <div className="bg-transparent px-4 py-3 flex flex-col justify-center">
                                <p className="text-[10px] font-semibold tracking-widest uppercase text-white/60">
                                    Cómputo
                                </p>
                                <p className="text-[10px] text-white/70 mt-0.5">Real − Contratado</p>
                            </div>
                            {data.employees.map((emp) => {
                                const delta = emp.delta_minutes;
                                const variant = delta === 0
                                    ? { bg: "rgba(110,231,183,0.20)", color: "#86EFAC", border: "rgba(110,231,183,0.6)" }
                                    : delta > 120 || delta < -120
                                        ? { bg: "rgba(252,165,165,0.25)", color: "#FCA5A5", border: "rgba(252,165,165,0.7)" }
                                        : { bg: "rgba(253,224,71,0.20)", color: "#FDE68A", border: "rgba(253,224,71,0.6)" };
                                return (
                                    <div
                                        key={`computo-${emp.employee_id}`}
                                        className="bg-transparent px-2 py-3 text-center"
                                        style={{
                                            color: variant.color,
                                            borderTop: `2px solid ${variant.border}`,
                                            background: variant.bg,
                                        }}
                                        data-testid={`computo-${emp.employee_id}`}
                                    >
                                        <p className="font-display font-bold text-base font-mono-zentra">
                                            {fmtHHMMSigned(delta)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="zentra-card-static p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                <span className="font-semibold uppercase tracking-wider text-zentra-ink">Leyenda:</span>
                <LegendChip color="#D1FAE5" border="#6EE7B7" label="Trabaja (calculado)" />
                <LegendChip color="#DBEAFE" border="#93C5FD" label="Manual" />
                <LegendChip color="#FEF3C7" border="#FCD34D" label="Vacaciones" />
                <LegendChip color="#FEE2E2" border="#FCA5A5" label="Baja" />
                <LegendChip color="#FED7AA" border="#FB923C" label="Permiso" />
                <LegendChip color="#F4F4F5" border="#D4D4D8" label="No puede / Libre" />
            </div>

            <CellEditModal
                edit={cellEdit}
                onClose={() => setCellEdit(null)}
                onSaved={() => {
                    setCellEdit(null);
                    load();
                }}
            />

            <ColorPickerModal
                emp={colorEdit}
                onClose={() => setColorEdit(null)}
                onSaved={() => {
                    setColorEdit(null);
                    load();
                }}
            />
        </div>
    );
}

function LegendChip({ color, border, label }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-zentra-muted">
            <span
                className="w-4 h-4 rounded"
                style={{ background: color, borderLeft: border ? `3px solid ${border}` : undefined }}
            />
            {label}
        </span>
    );
}

function CellEditModal({ edit, onClose, onSaved }) {
    const [status, setStatus] = useState("work");
    const [minutes, setMinutes] = useState(0);
    const [hoursStr, setHoursStr] = useState("0:00");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (edit) {
            const ov = edit.day.override;
            setStatus(ov?.status || "work");
            setMinutes(ov?.minutes || 0);
            setHoursStr(ov?.minutes ? fmtHHMM(ov.minutes) : "0:00");
            setNote(ov?.note || "");
            setError("");
        }
    }, [edit]);

    if (!edit) return null;
    const { emp, day, date } = edit;
    const dayDate = new Date(date);
    const dayName = dayDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

    const parseHoursStr = (s) => {
        const m = s.match(/^(\d+):(\d{1,2})$/);
        if (m) return parseInt(m[1]) * 60 + parseInt(m[2]);
        const f = parseFloat(s.replace(",", "."));
        if (!isNaN(f)) return Math.round(f * 60);
        return 0;
    };

    const submit = async () => {
        setSaving(true);
        setError("");
        try {
            const m = ["sick", "permission", "manual"].includes(status) ? parseHoursStr(hoursStr) : 0;
            await api.put("/schedule/override", {
                employee_id: emp.employee_id,
                date,
                status,
                minutes: m,
                note,
            });
            onSaved();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const showHoursField = ["sick", "permission", "manual"].includes(status);

    return (
        <Modal
            open={!!edit}
            onClose={onClose}
            title={`${emp.name} · ${dayName}`}
            subtitle={`Horas asignadas por limpieza ese día: ${fmtHHMM(day.assigned_minutes) || "0:00"}`}
            wide
            testId="cell-edit-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                    <button
                        type="button"
                        className="zentra-btn-primary"
                        onClick={submit}
                        disabled={saving}
                        data-testid="save-cell-override"
                    >
                        {saving ? "Guardando…" : "Guardar"}
                    </button>
                </>
            }
        >
            <div className="space-y-3" data-testid="cell-edit-options">
                {STATUS_OPTIONS.map((opt) => (
                    <label
                        key={opt.value}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                            status === opt.value
                                ? "border-zentra-primary bg-zentra-bg"
                                : "border-zentra-border hover:border-zentra-olive"
                        }`}
                    >
                        <input
                            type="radio"
                            name="status"
                            value={opt.value}
                            checked={status === opt.value}
                            onChange={() => setStatus(opt.value)}
                            className="mt-1"
                            data-testid={`status-${opt.value}`}
                        />
                        <opt.icon size={16} className="mt-0.5 text-zentra-primary" />
                        <div className="flex-1">
                            <p className="font-medium text-sm text-zentra-ink">{opt.label}</p>
                            <p className="text-xs text-zentra-muted">{opt.hint}</p>
                        </div>
                    </label>
                ))}

                {showHoursField && (
                    <div>
                        <label className="zentra-label">Horas a computar (formato H:MM o decimal)</label>
                        <input
                            className="zentra-input font-mono-zentra"
                            value={hoursStr}
                            onChange={(e) => setHoursStr(e.target.value)}
                            placeholder="5:00"
                            data-testid="cell-hours-input"
                        />
                        <p className="text-[11px] text-zentra-muted mt-1">
                            Ejemplos: <code>5:00</code>, <code>4:30</code>, <code>2.5</code>
                        </p>
                    </div>
                )}

                <div>
                    <label className="zentra-label">Nota (opcional)</label>
                    <input
                        className="zentra-input"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Asuntos personales, cita médica…"
                    />
                </div>

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
                )}
            </div>
        </Modal>
    );
}

function ColorPickerModal({ emp, onClose, onSaved }) {
    const [color, setColor] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (emp) setColor(emp.color);
    }, [emp]);

    if (!emp) return null;

    const save = async (c) => {
        setSaving(true);
        try {
            await api.patch(`/employees/${emp.employee_id}`, { color: c });
            onSaved();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={!!emp}
            onClose={onClose}
            title={`Color · ${emp.name}`}
            subtitle="Elige el color de cabecera para este empleado."
            testId="color-modal"
            footer={<button className="zentra-btn-ghost" onClick={onClose}>Cerrar</button>}
        >
            <div className="grid grid-cols-7 gap-2" data-testid="color-grid">
                {COLOR_PALETTE.map((c) => (
                    <button
                        key={c}
                        type="button"
                        disabled={saving}
                        onClick={() => save(c)}
                        className={`w-10 h-10 rounded-md border-2 transition ${
                            color === c ? "border-zentra-ink scale-110" : "border-transparent hover:scale-105"
                        }`}
                        style={{ background: c }}
                        data-testid={`color-${c.replace("#", "")}`}
                        title={c}
                    />
                ))}
            </div>
        </Modal>
    );
}
